import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Section, Inner, SectionHeader, SectionTitle, SectionSubtitle, Divider, Card,
} from '../styles/v2.styled';
import useLMSAuth, { isInstructor } from '../hooks/useLMSAuth';

const PageWrapper = styled.div`
  width: 100%; min-height: 100vh; background: ${({ theme }) => theme.colors.neutral[0]};
`;
const Hero = styled.section`
  padding: 70px 0 40px;
  background: linear-gradient(135deg, #312e81, #4338ca);
  color: #fff; text-align: center;
`;
const HeroTitle = styled.h1`font-size: 2.2rem; margin: 0 0 12px; font-weight: 800;`;
const HeroSub = styled.p`font-size: 1rem; opacity: 0.85;`;

const TopBar = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;`;
const NavBtn = styled.button`
  padding: 8px 16px; border: 1px solid #d1d5db; background: #fff; border-radius: 8px;
  font-size: 0.9rem; cursor: pointer;
`;
const PrimaryBtn = styled.button`
  padding: 10px 20px; font-size: 0.95rem; font-weight: 600; border-radius: 8px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary[500]}, ${({ theme }) => theme.colors.primary[700]});
  color: #fff; border: none; cursor: pointer;
`;
const MonthLabel = styled.div`font-size: 1.4rem; font-weight: 700; color: #111827;`;

const Calendar = styled.div`
  display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px;
  background: #e5e7eb; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden;
`;
const DayHead = styled.div`
  background: #f9fafb; padding: 10px; text-align: center; font-weight: 600; font-size: 0.85rem;
  color: ${({ $sun, $sat }) => $sun ? '#dc2626' : $sat ? '#2563eb' : '#374151'};
`;
const DayCell = styled.div`
  background: ${({ $today }) => $today ? '#ecfeff' : '#fff'};
  min-height: 110px; padding: 6px;
  display: flex; flex-direction: column;
  cursor: pointer;
  ${({ $other }) => $other && `opacity: 0.4;`}
  &:hover { background: #f3f4f6; }
`;
const DayNum = styled.div`
  font-size: 0.82rem; font-weight: 600;
  color: ${({ $sun, $sat }) => $sun ? '#dc2626' : $sat ? '#2563eb' : '#111827'};
  margin-bottom: 4px;
`;
const Event = styled.div`
  background: ${({ theme }) => theme.colors.primary[100]};
  color: ${({ theme }) => theme.colors.primary[800]};
  font-size: 0.7rem; padding: 2px 5px; border-radius: 4px;
  margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
`;
const ModalBox = styled.div`
  background: #fff; border-radius: 14px; padding: 28px; width: 90%; max-width: 520px; max-height: 90vh; overflow-y: auto;
`;
const Field = styled.div`margin-bottom: 14px;`;
const Label = styled.label`display:block; font-size: 0.85rem; font-weight: 600; margin-bottom: 6px; color: #374151;`;
const Input = styled.input`width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box;`;
const Select = styled.select`width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box;`;
const TextArea = styled.textarea`width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box; min-height: 70px;`;
const SmallBtn = styled.button`
  padding: 6px 12px; font-size: 0.78rem; border-radius: 6px;
  border: 1px solid ${({ $danger }) => $danger ? '#fecaca' : '#d1d5db'};
  background: ${({ $danger }) => $danger ? '#fee2e2' : '#fff'};
  color: ${({ $danger }) => $danger ? '#b91c1c' : '#374151'};
  cursor: pointer; margin-right: 6px;
`;

const empty = { course_id: '', title: '', description: '', start_at: '', end_at: '', location: '' };

function ymd(d) { return d.toISOString().slice(0, 10); }
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay();
  const daysInMonth = last.getDate();
  const cells = [];
  // 이전 달
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    cells.push({ date: d, other: true });
  }
  // 이번 달
  for (let i = 1; i <= daysInMonth; i++) {
    cells.push({ date: new Date(year, month, i), other: false });
  }
  // 다음 달 (총 42칸 채우기)
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, other: true });
  }
  return cells;
}

export default function SchedulePage() {
  const { role, login } = useLMSAuth();
  const [view, setView] = useState(() => {
    const t = new Date(); return { y: t.getFullYear(), m: t.getMonth() };
  });
  const [events, setEvents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [selDate, setSelDate] = useState(null);

  const range = useMemo(() => {
    const from = new Date(view.y, view.m, 1).toISOString();
    const to = new Date(view.y, view.m + 1, 0, 23, 59).toISOString();
    return { from, to };
  }, [view]);

  const load = async () => {
    const r = await fetch(`/lms/schedules?from=${range.from}&to=${range.to}`);
    if (r.ok) setEvents(await r.json());
    if (isInstructor(role) && courses.length === 0) {
      const c = await fetch('/lms/courses').then((rr) => rr.json());
      setCourses(c);
    }
  };
  useEffect(() => { load(); }, [view, role]);

  const cells = useMemo(() => buildMonthGrid(view.y, view.m), [view]);
  const eventsByDate = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      const k = ymd(new Date(e.start_at));
      (m[k] = m[k] || []).push(e);
    });
    return m;
  }, [events]);

  const today = ymd(new Date());

  const openNew = (date) => {
    if (!isInstructor(role)) return;
    const dt = date || new Date();
    const isoLocal = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}T19:00`;
    setEdit({ ...empty, start_at: isoLocal });
    setEditId(null); setOpen(true);
  };
  const openEdit = (ev) => {
    setEdit({
      course_id: ev.course_id || '',
      title: ev.title || '',
      description: ev.description || '',
      start_at: ev.start_at ? new Date(ev.start_at).toISOString().slice(0, 16) : '',
      end_at: ev.end_at ? new Date(ev.end_at).toISOString().slice(0, 16) : '',
      location: ev.location || '',
    });
    setEditId(ev.id); setOpen(true);
  };
  const save = async () => {
    if (!edit.title || !edit.start_at) { alert('제목과 시작시간은 필수입니다.'); return; }
    const url = editId ? `/lms/schedules/${editId}` : '/lms/schedules';
    const r = await fetch(url, {
      method: editId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...edit,
        course_id: edit.course_id ? Number(edit.course_id) : null,
      }),
    });
    if (!r.ok) { alert('저장 실패: ' + (await r.text())); return; }
    setOpen(false); load();
  };
  const remove = async () => {
    if (!editId) return;
    if (!window.confirm('일정을 삭제할까요?')) return;
    const r = await fetch(`/lms/schedules/${editId}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) { setOpen(false); load(); }
  };

  const prev = () => setView((v) => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });
  const next = () => setView((v) => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });
  const goToday = () => { const t = new Date(); setView({ y: t.getFullYear(), m: t.getMonth() }); };

  const weekHeads = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <PageWrapper>
      <Hero>
        <HeroTitle>강의 일정</HeroTitle>
        <HeroSub>월별 강의 일정을 한눈에 확인하세요</HeroSub>
      </Hero>

      <Section>
        <Inner>
          <TopBar>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <NavBtn onClick={prev}>← 이전</NavBtn>
              <NavBtn onClick={goToday}>오늘</NavBtn>
              <NavBtn onClick={next}>다음 →</NavBtn>
              <MonthLabel style={{ marginLeft: 16 }}>{view.y}년 {view.m + 1}월</MonthLabel>
            </div>
            {isInstructor(role) && (
              <PrimaryBtn onClick={() => openNew(null)}>+ 일정 등록</PrimaryBtn>
            )}
          </TopBar>

          <Calendar>
            {weekHeads.map((w, i) => (
              <DayHead key={w} $sun={i === 0} $sat={i === 6}>{w}</DayHead>
            ))}
            {cells.map((c, idx) => {
              const k = ymd(c.date);
              const evs = eventsByDate[k] || [];
              const dow = c.date.getDay();
              return (
                <DayCell key={idx} $other={c.other} $today={k === today}
                  onClick={() => isInstructor(role) && openNew(c.date)}>
                  <DayNum $sun={dow === 0} $sat={dow === 6}>{c.date.getDate()}</DayNum>
                  {evs.slice(0, 3).map((e) => (
                    <Event key={e.id} title={e.title}
                      onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}>
                      {new Date(e.start_at).getHours().toString().padStart(2, '0')}:00 {e.title}
                    </Event>
                  ))}
                  {evs.length > 3 && <Event style={{ background: '#e5e7eb', color: '#6b7280' }}>+{evs.length - 3}건</Event>}
                </DayCell>
              );
            })}
          </Calendar>

          <SectionHeader $align="left" style={{ marginTop: 32 }}>
            <Divider />
            <SectionTitle style={{ fontSize: '1.3rem' }}>이번 달 전체 일정</SectionTitle>
          </SectionHeader>
          {events.length === 0 ? (
            <Card style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>
              등록된 일정이 없습니다.
            </Card>
          ) : (
            events.map((e) => (
              <Card key={e.id} style={{ padding: 16, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                    {new Date(e.start_at).toLocaleString('ko-KR')} {e.location && '· ' + e.location}
                  </div>
                  <div style={{ fontWeight: 600, marginTop: 4 }}>{e.title}</div>
                  {e.course_title && <div style={{ fontSize: '0.83rem', color: '#0891b2' }}>📚 {e.course_title}</div>}
                </div>
                {isInstructor(role) && (
                  <SmallBtn onClick={() => openEdit(e)}>수정</SmallBtn>
                )}
              </Card>
            ))
          )}
        </Inner>
      </Section>

      {open && (
        <Modal onClick={() => setOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 18px' }}>{editId ? '일정 수정' : '일정 등록'}</h3>
            <Field><Label>제목</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Field>
            <Field><Label>관련 과정 (선택)</Label>
              <Select value={edit.course_id || ''} onChange={(e) => setEdit({ ...edit, course_id: e.target.value })}>
                <option value="">없음</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </Select>
            </Field>
            <Field><Label>시작</Label><Input type="datetime-local" value={edit.start_at} onChange={(e) => setEdit({ ...edit, start_at: e.target.value })} /></Field>
            <Field><Label>종료</Label><Input type="datetime-local" value={edit.end_at || ''} onChange={(e) => setEdit({ ...edit, end_at: e.target.value })} /></Field>
            <Field><Label>장소</Label><Input value={edit.location || ''} onChange={(e) => setEdit({ ...edit, location: e.target.value })} placeholder="온라인 ZOOM, 강의실 A 등" /></Field>
            <Field><Label>설명</Label><TextArea value={edit.description || ''} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {editId && <SmallBtn $danger onClick={remove} style={{ marginRight: 'auto' }}>삭제</SmallBtn>}
              <SmallBtn onClick={() => setOpen(false)}>취소</SmallBtn>
              <PrimaryBtn onClick={save}>저장</PrimaryBtn>
            </div>
          </ModalBox>
        </Modal>
      )}
    </PageWrapper>
  );
}
